import { useState, useMemo, useCallback, useEffect } from 'react'
import { paymentMethodsSimple } from '../../constant/ameber-pricing'
import { AUTH_SIGNUP_URL_PRICING } from '../../constant/auth'
import { usePricingPlans } from '../../hooks/usePricingPlans'
import { periodFormat } from '../../utils/utils'
import Tabs from '../tabs'

interface IProps {
  isLoggedIn: boolean
}

export default function PricingForm(props: IProps) {
  const { isLoggedIn } = props
  const { isLoadingPricing, data: plansData } = usePricingPlans()

  const [selectedSubscription, setSelectedSubscription] = useState<number>(1)
  const [selectedPlan, setSelectedPlan] = useState<number>(0)
  const [selectedPayment, setSelectedPayment] = useState<string>('')
  const availablePlansNames = useMemo(() => {
    return plansData?.map((plan) => plan.period) || []
  }, [plansData])

  const selectedPlanPrice = useMemo(
    () =>
      plansData?.[selectedSubscription].products?.find((p) => p.product_id === selectedPlan)?.price,
    [plansData, selectedSubscription, selectedPlan]
  )

  const handleChangeSubscription = useCallback(
    (id: number) => {
      setSelectedSubscription(id)
      setSelectedPlan(plansData?.[id]?.products[0]?.product_id || 0)
      setSelectedPayment(plansData?.[id]?.products[0]?.allowed_paysys[0] || '')
    },
    [selectedSubscription, plansData, selectedPlan]
  )

  const paymentMethods = useMemo(() => {
    const allowedPaysys = plansData?.[selectedSubscription]?.products.find(
      (p) => p.product_id === selectedPlan
    )?.allowed_paysys

    if (!allowedPaysys) return []

    return allowedPaysys
      .map((methodId) => {
        const matched = paymentMethodsSimple.find((pm) => methodId.includes(pm.id))
        return matched && { ...matched, id: methodId }
      })
      .filter(Boolean)
  }, [selectedSubscription, selectedPlan, plansData])

  const handleChangePlan = useCallback(
    (id: number) => {
      setSelectedPlan(id)
      setSelectedPayment(
        plansData?.[selectedSubscription]?.products.find((p) => p.product_id === id)
          ?.allowed_paysys[0] || ''
      )
    },
    [selectedPlan, plansData]
  )

  useEffect(() => {
    if (plansData?.length) {
      setSelectedPlan(plansData[1].products[0].product_id)
      setSelectedPayment(plansData?.[1]?.products[0]?.allowed_paysys[0])
    }
  }, [plansData])

  const handleSubmit = useCallback(() => {
    const form = document.createElement('form')
    form.method = 'POST'
    form.action = '/crm/signup/index'
    form.style.display = 'none'

    const appendInput = (name: string, value: string) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = name
      input.value = value
      form.appendChild(input)
    }

    appendInput('product_id_page-0[]', `${selectedPlan}-${selectedPlan}`)
    appendInput('paysys_id', selectedPayment)
    appendInput('_qf_page-0_next', '1')
    appendInput('_save_', 'page-0')
    appendInput('data-saved_form_id', '1')
    appendInput('source_domain', window.location.host)

    document.body.appendChild(form)
    form.submit()
  }, [selectedPlan, selectedPayment])

  return (
    <div className="md:max-w-[500px] px-4 md-px-0 w-full md:mt-5 mt-4">
      {availablePlansNames.length > 0 && (
        <Tabs
          tabs={availablePlansNames}
          activeTab={selectedSubscription}
          onTabChange={handleChangeSubscription}
        />
      )}
      <div className="mt-6 text-center text-lg">
        {plansData?.[selectedSubscription]?.products?.map((option) => (
          <div
            key={option.product_id}
            className={`bg-opacity-20 transition-all backdrop-filter-[30] backdrop-blur-sm rounded-lg mb-2 p-3 cursor-pointer ${selectedPlan === option.product_id ? 'inset-shadow inset-shadow-greenActive ring-2 ring-greenActive bg-greenActive bg-opacity-25' : 'bg-white '}`}
            onClick={() => handleChangePlan(option.product_id)}
          >
            <div className="flex gap-2 items-start">
              <div
                className={`relative w-6 h-6 rounded-full border-[1px] mt-1 ${selectedPlan === option.product_id ? 'border-cyan' : 'border-white'}`}
              >
                {selectedPlan === option.product_id && (
                  <span className="absolute w-[16px] h-[16px] bg-greenActive rounded-full top-[3px] left-[3px]"></span>
                )}
              </div>
              <div className="text-left flex-1">
                <div className="text-left flex justify-between flex-1">
                  <p
                    className={`text-white ${selectedPlan === option.product_id ? 'font-poppinsBold' : 'font-poppinsRegular'} text-md`}
                  >
                    {option.categories[0].title} package
                  </p>
                  <p
                    className={`text-white flex gap-1 ${selectedPlan === option.product_id ? 'font-rubikSemiBold' : 'font-rubikRegular'} ml-auto`}
                  >
                    <span>$</span>
                    {option.price}/{periodFormat(option.period)}
                  </p>
                </div>
                <div className="text-gray text-sm font-poppinsRegular max-w-[200px] md:max-w-[300px]">
                  {option.description}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {isLoggedIn ? (
        !isLoadingPricing && (
          <div className="w-full">
            <p className="relative font-rubikRegular text-white mb-4 mt-7">Payment Method</p>
            {paymentMethods.map((method) => {
              if (method && method.id) {
                return (
                  <div
                    key={method?.id}
                    className={`transition-all w-full bg-opacity-20 backdrop-filter-[30] backdrop-blur-sm rounded-lg mb-2 p-3 cursor-pointer ${selectedPayment.includes(method.id) ? 'inset-shadow inset-shadow-greenActive ring-2 ring-greenActive bg-greenActive bg-opacity-25' : 'bg-white '}`}
                    onClick={() => setSelectedPayment(method.id)}
                  >
                    <div className="flex gap-2 items-center w-full">
                      <div
                        className={`relative w-[24px] h-[24px] rounded-full border-[1px] ${selectedPayment.includes(method.id) ? 'border-cyan' : 'border-white'}`}
                      >
                        {selectedPayment.includes(method.id) && (
                          <span className="absolute w-[16px] h-[16px] bg-greenActive rounded-full top-[3px] left-[3px]"></span>
                        )}
                      </div>
                      <span className="flex flex-1 items-center justify-between text-white">
                        <p
                          className={`${selectedPayment.includes(method.id) ? 'font-poppinsBold' : 'font-poppinsRegular'} text-md`}
                        >
                          {method.label}
                        </p>
                        <div className="flex gap-2 items-center ml-auto">
                          {method.logos.map((logo) => (
                            <img
                              key={logo.img}
                              className="h-4 w-auto"
                              src={logo.img}
                              alt={logo.alt}
                            />
                          ))}
                        </div>
                      </span>
                    </div>
                  </div>
                )
              }
            })}
            <div
              style={{
                display: paymentMethodsSimple.length ? 'none' : 'block',
              }}
            >
              No Payment Methods Available
            </div>

            <button
              className="relative w-full h-12 mt-12 rounded-lg text-white font-rubikSemiBold bg-greenActive"
              onClick={handleSubmit}
            >
              Subscribe for $ {selectedPlanPrice}
            </button>
          </div>
        )
      ) : (
        <div className="flex flex-col items-center mt-4">
          <div className="logos text-white pb-2">
            <img
              className="logos__img"
              src={'/icons/amember/mastercard.svg'}
              alt={'mastercard'}
              height="24"
            />
            <img
              className="logos__img"
              src={'/icons/amember/visa-white.svg'}
              alt={'visa'}
              height="24"
            />
            <img
              className="logos__img"
              src={'/icons/amember/american-express-full.svg'}
              alt={'american-express'}
              height="24"
            />
            <img
              className="logos__img"
              src={'/icons/amember/paypal-white.svg'}
              alt={'paypal'}
              height="24"
            />
            <img
              className="logos__img"
              src={'/icons/amember/bitcoin.svg'}
              alt={'bitcoin'}
              height="24"
            />
            Crypto
          </div>

          <a href={AUTH_SIGNUP_URL_PRICING} className="payment-system__button font-rubikSemiBold">
            Sign up to continue
          </a>
        </div>
      )}
    </div>
  )
}
